import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, CreditCard, Lock, AlertCircle, Edit3 } from 'lucide-react';
import { Logo } from './Logo';
import { callWFAPI } from '../utils/helper';
interface Invoice {
  id: string;
  invoiceNumber: string;
  customerName: string;
  customerEmail: string;
  customerAddress?: string;
  customerAddress2?: string;
  customerCity?: string;
  customerState?: string;
  customerZip?: string;
  lineItems: Array<{
    description: string;
    quantity: number;
    price: number;
    total: number;
  }>;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  amountPaid: number;
  balance: number;
  status: string;
  distributorName?: string;
  distributorId?: string;
  signature?: string;
}
export function PaymentPage() {
  const {
    invoiceId
  } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [cardHolderName, setCardHolderName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [error, setError] = useState('');
  const [stripeError, setStripeError] = useState('');
  const [loading, setLoading] = useState(true);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [stripe, setStripe] = useState<any>(null);
  const [cardElement, setCardElement] = useState<any>(null);
  const [agreementChecks, setAgreementChecks] = useState({
    powerStrip: false,
    technicianOperation: false,
    lostStolen: false,
    hardwareCount: false
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState<string>('');
  useEffect(() => {
    loadInvoice();
  }, [invoiceId]);
  const loadInvoice = async () => {
    console.log('Loading invoice with ID:', invoiceId);
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Rental_Invoices',
        criteria: `ID == ${invoiceId}`
      };
      const invoices = await callWFAPI(0, 'zohoCreator', data);
      console.log('Invoice response from database:', invoices);
      if (invoices && invoices.length > 0) {
        const invoiceDetails = invoices[0];
        // Fetch shipping address from the related rental record
        let shippingAddress = null;
        if (invoiceDetails.Gen_3_Scoring_Rental?.ID) {
          try {
            const rentalData = {
              appname: 'tkd-score',
              reportname: 'Gen_3_Scoring_Rental_Report',
              criteria: `ID == ${invoiceDetails.Gen_3_Scoring_Rental.ID}`
            };
            const rentals = await callWFAPI(0, 'zohoCreator', rentalData);
            if (rentals && rentals.length > 0) {
              shippingAddress = rentals[0].Shipping_Address;
              console.log('Fetched shipping address from rental:', shippingAddress);
            }
          } catch (rentalError) {
            console.error('Error fetching rental shipping address:', rentalError);
          }
        }
        // Parse line items from display_value format
        const lineItems = (invoiceDetails.Items || []).map((item: any) => {
          const parts = item.display_value.split('||');
          const title = parts[0] || '';
          const quantity = parseInt(parts[1]) || 0;
          const price = parseFloat(parts[2]) || 0;
          return {
            description: title,
            quantity: quantity,
            price: price,
            total: quantity * price
          };
        });
        // Calculate status based on payment amounts
        const totalAmount = parseFloat(invoiceDetails.Total_Amount) || 0;
        const paidAmount = parseFloat(invoiceDetails.Paid_Amount) || 0;
        const balance = totalAmount - paidAmount;
        let status = 'Not Paid';
        if (paidAmount > 0 && balance > 0.01) {
          status = 'Partially Paid';
        } else if (balance <= 0.01) {
          status = 'Paid';
        }
        // Transform the database response to match our Invoice interface
        const transformedInvoice: Invoice = {
          id: invoiceDetails.ID,
          invoiceNumber: invoiceDetails.Invoice_Number || invoiceDetails.Number,
          customerName: invoiceDetails.Customer_Name,
          customerEmail: invoiceDetails.Customer_Email,
          customerAddress: shippingAddress?.address_line_1 || undefined,
          customerAddress2: shippingAddress?.address_line_2 || undefined,
          customerCity: shippingAddress?.district_city || undefined,
          customerState: shippingAddress?.state_province || undefined,
          customerZip: shippingAddress?.postal_code || undefined,
          lineItems: lineItems,
          subtotal: parseFloat(invoiceDetails.Subtotal) || 0,
          discount: parseFloat(invoiceDetails.Discount_Amount) || 0,
          tax: parseFloat(invoiceDetails.California_Tax) || 0,
          total: totalAmount,
          amountPaid: paidAmount,
          balance: balance,
          status: status,
          distributorName: invoiceDetails.Distributor?.display_value || undefined,
          distributorId: invoiceDetails.Distributor?.ID || undefined,
          signature: invoiceDetails.Signature || undefined
        };
        console.log('Transformed invoice:', transformedInvoice);
        console.log('Shipping address used:', shippingAddress);
        setInvoice(transformedInvoice);
        setPaymentAmount(transformedInvoice.balance.toFixed(2));
        // If signature exists, set it and check all boxes
        if (transformedInvoice.signature) {
          setSignatureData(transformedInvoice.signature);
          setAgreementChecks({
            powerStrip: true,
            technicianOperation: true,
            lostStolen: true,
            hardwareCount: true
          });
        }
      } else {
        console.error('Invoice not found in database');
      }
    } catch (error) {
      console.error('Error loading invoice:', error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // Load Stripe script
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.onload = () => setStripeLoaded(true);
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  useEffect(() => {
    // Initialize Stripe after invoice is loaded and script is ready
    if (stripeLoaded && invoice && invoice.distributorName) {
      initializeStripe();
    }
  }, [stripeLoaded, invoice]);
  const initializeStripe = async () => {
    try {
      // Fetch distributor's Stripe public key
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Distributors',
        criteria: `ID == ${invoice?.distributorId}`
      };
      const distributors = await callWFAPI(0, 'zohoCreator', data);
      if (!distributors || distributors.length === 0) {
        console.error('Distributor not found');
        return;
      }
      const stripePublicKey = distributors[0].Stripe_Public_Key;
      if (!stripePublicKey) {
        console.error('Stripe public key not found for distributor');
        return;
      }
      // Initialize Stripe
      const stripeInstance = (window as any).Stripe(stripePublicKey);
      setStripe(stripeInstance);
      // Create card element
      const elements = stripeInstance.elements();
      const card = elements.create('card', {
        style: {
          base: {
            color: '#32325d',
            fontFamily: '"Inter", sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': {
              color: '#aab7c4'
            }
          },
          invalid: {
            color: '#fa755a',
            iconColor: '#fa755a'
          }
        }
      });
      card.mount(document.getElementById('card_number'));
      setCardElement(card);
      card.on('change', (event: any) => {
        if (event.error) {
          setStripeError(event.error.message);
        } else {
          setStripeError('');
        }
      });
    } catch (error) {
      console.error('Error initializing Stripe:', error);
    }
  };
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ('touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left) * scaleX;
    const y = ('touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top) * scaleY;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(x, y);
    }
  };
  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ('touches' in e ? e.touches[0].clientX - rect.left : e.clientX - rect.left) * scaleX;
    const y = ('touches' in e ? e.touches[0].clientY - rect.top : e.clientY - rect.top) * scaleY;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.lineTo(x, y);
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
  };
  const stopDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureData(canvas.toDataURL());
    }
  };
  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
    setSignatureData('');
  };
  useEffect(() => {
    // Initialize canvas
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      // If signature exists, draw it
      if (invoice?.signature) {
        const img = new Image();
        img.onload = () => {
          ctx?.drawImage(img, 0, 0);
        };
        img.src = invoice.signature;
      }
    }
  }, [invoice]);
  const processPayment = async () => {
    if (!invoice) return;
    // Signature is ALWAYS required - either existing or new
    if (!signatureData && !invoice.signature) {
      setError('Please provide your signature before proceeding');
      return;
    }
    // Check if all agreement items are checked
    const allChecked = Object.values(agreementChecks).every(checked => checked);
    if (!allChecked) {
      setError('Please acknowledge all rental agreement items before proceeding');
      return;
    }
    // If zero-dollar invoice, just mark as paid with signature
    if (invoice.total === 0) {
      await markZeroDollarInvoicePaid();
      return;
    }
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }
    if (amount > invoice.balance) {
      setError('Payment amount cannot exceed balance due');
      return;
    }
    if (!cardHolderName.trim()) {
      setError('Please enter card holder name');
      return;
    }
    if (!stripe || !cardElement) {
      setError('Payment system not ready. Please refresh the page.');
      return;
    }
    setIsProcessing(true);
    setError('');
    setStripeError('');
    try {
      // Create Stripe Payment method
      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: cardHolderName
        }
      });
      if (result.error) {
        setStripeError(result.error.message);
        setIsProcessing(false);
        return;
      }
      const paymentMethodId = result.paymentMethod.id;
      const cardBrand = result.paymentMethod.card.brand;
      const cardExp = result.paymentMethod.card.exp_month + '/' + result.paymentMethod.card.exp_year;
      const cardLast4 = result.paymentMethod.card.last4;
      // Create our payment record
      const paymentParams = {
        Distributor: invoice.distributorId,
        Type_field: 'Stripe',
        Customer_Email: invoice.customerEmail,
        Customer_Name: invoice.customerName,
        Stripe_Method_ID: paymentMethodId,
        Amount: amount.toString(),
        Description: invoice.invoiceNumber,
        Card_Brand: cardBrand,
        Card_Exp: cardExp,
        Card_Last_Digits: cardLast4,
        Status: 'process'
      };
      const params = {
        appname: 'tkd-score',
        formname: 'Rental_Payment',
        params: JSON.stringify(paymentParams)
      };
      const paymentResponse = await callWFAPI(0, 'zohoCreator', params);
      // Verify payment status using the specific action API
      try {
        await callWFAPI(0, 'specificAction', {
          action: 'tkdscoreRentalPayment',
          payment_id: paymentResponse.ID
        });
        // If no exception was raised, payment was processed successfully
        // Update "Paid Amount" of invoice - wrapped in try-catch to prevent double payments
        try {
          const newPaidAmount = invoice.amountPaid + amount;
          // Determine if we need to save signature (first payment)
          const isFirstPayment = invoice.amountPaid === 0;
          const updateData = {
            appname: 'tkd-score',
            reportname: 'All_Rental_Invoices',
            id: invoice.id,
            params: JSON.stringify({
              Paid_Amount: newPaidAmount,
              ...(isFirstPayment && signatureData ? {
                Signature: signatureData
              } : {})
            })
          };
          await callWFAPI(0, 'zohoCreator', updateData);
          // Update local invoice state
          const newBalance = invoice.total - newPaidAmount;
          const updatedInvoice = {
            ...invoice,
            amountPaid: newPaidAmount,
            balance: newBalance,
            status: newBalance <= 0.01 ? 'Paid in Full' : 'Partial Payment',
            ...(isFirstPayment && signatureData ? {
              signature: signatureData
            } : {})
          };
          setInvoice(updatedInvoice);
          setPaymentComplete(true);
        } catch (updateError) {
          console.error('Error updating invoice after payment:', updateError);
          // Payment succeeded but invoice update failed - log this critical error
          setError('Payment processed but invoice update failed. Please contact support with payment ID: ' + paymentResponse.ID);
        } finally {
          setIsProcessing(false);
        }
      } catch (verificationError) {
        // If the API call raised an exception, payment failed
        console.error('Payment verification failed:', verificationError);
        setError('Payment failed. Please try again.');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError('Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };
  const markZeroDollarInvoicePaid = async () => {
    if (!invoice) return;
    setIsProcessing(true);
    setError('');
    try {
      // Update invoice with signature
      const updateData = {
        appname: 'tkd-score',
        reportname: 'All_Rental_Invoices',
        id: invoice.id,
        params: JSON.stringify({
          Signature: signatureData
        })
      };
      await callWFAPI(0, 'zohoCreator', updateData);
      // Update local invoice state
      const updatedInvoice = {
        ...invoice,
        signature: signatureData,
        status: 'Paid in Full'
      };
      setInvoice(updatedInvoice);
      setIsProcessing(false);
      setPaymentComplete(true);
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      setError('Failed to mark invoice as paid. Please try again.');
      setIsProcessing(false);
    }
  };
  if (loading) {
    return <div className="min-h-screen bg-slate-100 flex items-center justify-center" data-id="element-1337">
        <div className="text-center" data-id="element-1338">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" data-id="element-1339"></div>
          <p className="text-slate-600" data-id="element-1340">Loading invoice...</p>
        </div>
      </div>;
  }
  if (!invoice) {
    return <div className="min-h-screen bg-slate-100" data-id="element-1341">
        <div className="bg-white shadow-sm border-b border-slate-200" data-id="element-1342">
          <div className="max-w-4xl mx-auto px-6 py-4" data-id="element-1343">
            <Logo data-id="element-1344" />
          </div>
        </div>
        <div className="max-w-4xl mx-auto p-6 mt-8" data-id="element-1345">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center" data-id="element-1346">
            <AlertCircle size={48} className="text-red-400 mx-auto mb-4" data-id="element-1347" />
            <h1 className="text-2xl font-bold text-slate-800 mb-2" data-id="element-1348">
              Invoice Not Found
            </h1>
            <p className="text-slate-600 mb-4" data-id="element-1349">
              The invoice you're looking for doesn't exist or the link is
              invalid.
            </p>
            <p className="text-sm text-slate-500 mb-4" data-id="element-1350">
              Invoice ID: {invoiceId}
            </p>
            <button onClick={() => window.location.href = '/'} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-1351">
              Go to Home
            </button>
          </div>
        </div>
      </div>;
  }
  const isPaidInFull = invoice.balance <= 0.01 && invoice.signature;
  const isZeroDollarInvoice = invoice.total === 0;
  const hasBalanceDue = invoice.balance > 0.01;
  return <div className="min-h-screen bg-slate-100" data-id="element-1352">
      <div className="bg-white shadow-sm border-b border-slate-200" data-id="element-1353">
        <div className="max-w-4xl mx-auto px-6 py-4" data-id="element-1354">
          <Logo data-id="element-1355" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 mt-8" data-id="element-1356">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1357">
          {/* Invoice Details */}
          <div className="lg:col-span-2" data-id="element-1358">
            <div className="bg-white rounded-lg shadow-lg p-6" data-id="element-1359">
              <div className="flex justify-between items-start mb-6" data-id="element-1360">
                <div data-id="element-1361">
                  <h2 className="text-2xl font-bold text-slate-800 mb-1" data-id="element-1362">
                    Invoice Details
                  </h2>
                  <p className="text-slate-600" data-id="element-1363">
                    Invoice #{invoice.invoiceNumber}
                  </p>
                </div>
                {invoice.status === 'Paid' && <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium" data-id="element-1364">
                    Paid in Full
                  </span>}
                {invoice.status === 'Partially Paid' && <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium" data-id="element-1365">
                    Partially Paid
                  </span>}
              </div>

              <div className="mb-6" data-id="element-1366">
                <h2 className="text-sm font-medium text-slate-500 mb-1" data-id="element-1367">
                  Bill To
                </h2>
                <p className="font-semibold text-slate-800" data-id="element-1368">
                  {invoice.customerName}
                </p>
                <p className="text-slate-600" data-id="element-1369">{invoice.customerEmail}</p>
                {(invoice.customerAddress || invoice.customerCity) && <div className="mt-2 text-sm text-slate-600 space-y-0.5" data-id="element-1370">
                    {invoice.customerAddress && <p data-id="element-1371">{invoice.customerAddress}</p>}
                    {invoice.customerAddress2 && <p data-id="element-1372">{invoice.customerAddress2}</p>}
                    {(invoice.customerCity || invoice.customerState || invoice.customerZip) && <p data-id="element-1373">
                        {[invoice.customerCity, invoice.customerState, invoice.customerZip].filter(Boolean).join(', ')}
                      </p>}
                  </div>}
              </div>

              <div className="border-t border-slate-200 pt-6" data-id="element-1374">
                <table className="w-full" data-id="element-1375">
                  <thead data-id="element-1376">
                    <tr className="border-b border-slate-200" data-id="element-1377">
                      <th className="text-left py-2 text-sm font-medium text-slate-600" data-id="element-1378">
                        Description
                      </th>
                      <th className="text-right py-2 text-sm font-medium text-slate-600" data-id="element-1379">
                        Qty
                      </th>
                      <th className="text-right py-2 text-sm font-medium text-slate-600" data-id="element-1380">
                        Price
                      </th>
                      <th className="text-right py-2 text-sm font-medium text-slate-600" data-id="element-1381">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1382">
                    {invoice.lineItems.map((item, index) => <tr key={index} className="border-b border-slate-100" data-id="element-1383">
                        <td className="py-3 text-slate-800" data-id="element-1384">
                          {item.description}
                        </td>
                        <td className="py-3 text-right text-slate-600" data-id="element-1385">
                          {item.quantity}
                        </td>
                        <td className="py-3 text-right text-slate-600" data-id="element-1386">
                          ${item.price.toFixed(2)}
                        </td>
                        <td className="py-3 text-right font-medium text-slate-800" data-id="element-1387">
                          ${item.total.toFixed(2)}
                        </td>
                      </tr>)}
                  </tbody>
                </table>

                <div className="mt-6 space-y-2" data-id="element-1388">
                  <div className="flex justify-between text-slate-700" data-id="element-1389">
                    <span data-id="element-1390">Subtotal</span>
                    <span data-id="element-1391">${invoice.subtotal.toFixed(2)}</span>
                  </div>
                  {invoice.discount > 0 && <div className="flex justify-between text-green-600" data-id="element-1392">
                      <span data-id="element-1393">Discount</span>
                      <span data-id="element-1394">-${invoice.discount.toFixed(2)}</span>
                    </div>}
                  {invoice.tax > 0 && <div className="flex justify-between text-slate-700" data-id="element-1395">
                      <span data-id="element-1396">Tax</span>
                      <span data-id="element-1397">${invoice.tax.toFixed(2)}</span>
                    </div>}
                  <div className="flex justify-between font-bold text-lg text-slate-900 pt-2 border-t border-slate-200" data-id="element-1398">
                    <span data-id="element-1399">Total</span>
                    <span data-id="element-1400">${invoice.total.toFixed(2)}</span>
                  </div>
                  {invoice.amountPaid > 0 && <>
                      <div className="flex justify-between text-green-600 pt-2" data-id="element-1401">
                        <span data-id="element-1402">Amount Paid</span>
                        <span className="font-semibold" data-id="element-1403">
                          ${invoice.amountPaid.toFixed(2)}
                        </span>
                      </div>
                      {!isPaidInFull && <div className="flex justify-between font-bold text-xl text-red-600 pt-2 border-t border-slate-200" data-id="element-1404">
                          <span data-id="element-1405">Balance Due</span>
                          <span data-id="element-1406">${invoice.balance.toFixed(2)}</span>
                        </div>}
                    </>}
                </div>
              </div>

              {/* Rental Agreement */}
              {!isPaidInFull && hasBalanceDue && <div className="mt-6 border-t border-slate-200 pt-6" data-id="element-1407">
                  <h3 className="text-lg font-bold text-slate-800 mb-4" data-id="element-1408">
                    {isZeroDollarInvoice ? 'Complete Agreement' : 'Make a Payment'}
                  </h3>

                  {/* Show info banner for zero-dollar invoices */}
                  {isZeroDollarInvoice && <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1409">
                      <p className="text-sm text-blue-800" data-id="element-1410">
                        <strong data-id="element-1411">No payment required.</strong> Please review and
                        sign the rental agreement below to complete this
                        invoice.
                      </p>
                    </div>}

                  {/* Only show payment fields if NOT a zero-dollar invoice */}
                  {!isZeroDollarInvoice && <>
                      <div className="mb-4" data-id="element-1412">
                        <div className="flex justify-between items-center mb-4" data-id="element-1413">
                          <h4 className="text-lg font-medium text-slate-800" data-id="element-1414">
                            Payment Amount
                          </h4>
                          <span className="text-slate-600" data-id="element-1415">
                            ${invoice.balance.toFixed(2)}
                          </span>
                        </div>
                        <input type="number" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-800" placeholder="Enter payment amount" data-id="element-1416" />
                        <p className="text-xs text-slate-500 mt-1" data-id="element-1417">
                          Maximum: ${invoice.balance.toFixed(2)}
                        </p>
                      </div>

                      <div className="mb-4" data-id="element-1418">
                        <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1419">
                          Card Holder Name
                        </label>
                        <input type="text" value={cardHolderName} onChange={e => setCardHolderName(e.target.value)} placeholder="John Doe" className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1420" />
                      </div>

                      <div className="mb-4" data-id="element-1421">
                        <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1422">
                          Card Number
                        </label>
                        <div id="card_number" className="w-full px-3 py-3 border border-slate-300 rounded-lg" data-id="element-1423"></div>
                        {stripeError && <p className="text-xs text-red-600 mt-1" data-id="element-1424">
                            {stripeError}
                          </p>}
                      </div>
                    </>}

                  {/* Rental Agreement Checkboxes */}
                  <div className="mb-6" data-id="element-1425">
                    <label className="block text-sm font-medium text-slate-700 mb-3" data-id="element-1426">
                      Rental Agreement <span className="text-red-500" data-id="element-1427">*</span>
                    </label>
                    <div className="space-y-3 bg-slate-50 border border-slate-200 rounded-lg p-4" data-id="element-1428">
                      <label className="flex items-start" data-id="element-1429">
                        <input type="checkbox" checked={agreementChecks.powerStrip} onChange={() => setAgreementChecks({
                      ...agreementChecks,
                      powerStrip: !agreementChecks.powerStrip
                    })} className="w-4 h-4 mt-1 rounded text-blue-600 focus:ring-blue-500" data-id="element-1430" />
                        <span className="ml-2 text-sm text-slate-700" data-id="element-1431">
                          I will provide a power strip for the equipment
                        </span>
                      </label>
                      <label className="flex items-start" data-id="element-1432">
                        <input type="checkbox" checked={agreementChecks.technicianOperation} onChange={() => setAgreementChecks({
                      ...agreementChecks,
                      technicianOperation: !agreementChecks.technicianOperation
                    })} className="w-4 h-4 mt-1 rounded text-blue-600 focus:ring-blue-500" data-id="element-1433" />
                        <span className="ml-2 text-sm text-slate-700" data-id="element-1434">
                          I understand only the technician may operate the
                          equipment
                        </span>
                      </label>
                      <label className="flex items-start" data-id="element-1435">
                        <input type="checkbox" checked={agreementChecks.lostStolen} onChange={() => setAgreementChecks({
                      ...agreementChecks,
                      lostStolen: !agreementChecks.lostStolen
                    })} className="w-4 h-4 mt-1 rounded text-blue-600 focus:ring-blue-500" data-id="element-1436" />
                        <span className="ml-2 text-sm text-slate-700" data-id="element-1437">
                          I am responsible for lost or stolen equipment
                        </span>
                      </label>
                      <label className="flex items-start" data-id="element-1438">
                        <input type="checkbox" checked={agreementChecks.hardwareCount} onChange={() => setAgreementChecks({
                      ...agreementChecks,
                      hardwareCount: !agreementChecks.hardwareCount
                    })} className="w-4 h-4 mt-1 rounded text-blue-600 focus:ring-blue-500" data-id="element-1439" />
                        <span className="ml-2 text-sm text-slate-700" data-id="element-1440">
                          I will verify hardware count before the technician
                          leaves
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Signature */}
                  <div className="mb-6" data-id="element-1441">
                    <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1442">
                      Signature <span className="text-red-500" data-id="element-1443">*</span>
                    </label>
                    <div className="border-2 border-slate-300 rounded-lg overflow-hidden bg-white" data-id="element-1444">
                      <div className="flex justify-between items-center px-4 py-2 bg-slate-50 border-b border-slate-200" data-id="element-1445">
                        <span className="text-sm text-slate-600" data-id="element-1446">
                          Sign below
                        </span>
                        <button onClick={clearSignature} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-id="element-1447">
                          Clear
                        </button>
                      </div>
                      <canvas ref={canvasRef} className="w-full cursor-crosshair" style={{
                    height: '150px',
                    touchAction: 'none'
                  }} width={600} height={150} onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} data-id="element-1448" />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button onClick={processPayment} disabled={isProcessing} className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 ${isProcessing ? 'bg-slate-400 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'} transition-colors`} data-id="element-1449">
                    {isProcessing ? <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" data-id="element-1450"></div>
                        Processing...
                      </> : isZeroDollarInvoice ? <>
                        <CheckCircle size={20} data-id="element-1451" />
                        Complete Agreement
                      </> : <>
                        <CreditCard size={20} data-id="element-1452" />
                        Pay ${paymentAmount}
                      </>}
                  </button>

                  {/* Secure payment notice (only for non-zero invoices) */}
                  {!isZeroDollarInvoice && <div className="mt-4 flex items-center justify-center text-xs text-slate-500" data-id="element-1453">
                      <Lock size={12} className="mr-1" data-id="element-1454" />
                      Secure payment powered by Stripe
                    </div>}

                  {/* Error messages */}
                  {error && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-id="element-1455">
                      {error}
                    </div>}

                  {stripeError && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-1456">
                      {stripeError}
                    </div>}

                  {/* Distributor info (only for non-zero invoices) */}
                  {!isZeroDollarInvoice && invoice.distributorName && <div className="mt-4 text-xs text-slate-600 text-center" data-id="element-1457">
                      Distributor:{' '}
                      <span className="font-medium" data-id="element-1458">
                        {invoice.distributorName}
                      </span>
                    </div>}
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}